import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';

import { Stack, Text } from '@lp/ds-next';
import forEach from 'lodash/forEach';
import styled from 'styled-components';

import CountrySelector from './forms/CountrySelector';
import SearchField from './forms/SearchField';
import PartnerAutocomplete from './ParnterAutocomplete';
import SearchButton from './SearchButton';
import SearchResetButton from './SearchResetButton';
import useShipmentsStore from '@/features/shipments/hooks/useShipmentsStore';

interface IField {
  label?: string;
  placeholder?: string;
  key: string;
}

interface IServerSearch {
  fields: IField[];
  dispatch: any;
  storeSearchTerms: {
    [key: string]: any;
  };
  label?: string;
  setSelectedRowKeys?: (keys: any[]) => void;
  extraButtons?: React.ReactNode;
}

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MAX_COLUMNS = 5;

const FieldsGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  flex: 1;
  gap: 0.5rem 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonsColumn = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding-top: 2px;
  flex-shrink: 0;
`;

const trimSearchTerms = (searchTerms: Record<string, any>) => {
  const result: Record<string, any> = {};
  forEach(searchTerms, (value, key) => {
    result[key] = !Array.isArray(value) ? value.trim() : value;
  });

  return result;
};

const getURLSearchParams = (): Record<string, any> => {
  if (typeof window === 'undefined') return {};

  const searchParams = new URLSearchParams(window.location.search);
  const params: Record<string, any> = {};

  for (const [key, value] of searchParams.entries()) {
    if (value) {
      try {
        params[key] = JSON.parse(value);
      } catch {
        params[key] = value;
      }
    }
  }

  return params;
};

const updateURLParams = (params: Record<string, string>) => {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value && value !== '') {
      searchParams.set(
        key,
        typeof value === 'string' ? value : JSON.stringify(value)
      );
    }
  });

  const newSearch = searchParams.toString();
  const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
  window.history.replaceState({}, '', newUrl);
};

const ServerSearch = ({
  fields,
  dispatch,
  storeSearchTerms,
  label = 'Search by:',
  setSelectedRowKeys = undefined,
  extraButtons,
}: IServerSearch) => {
  const [initialParams] = useState(getURLSearchParams);
  const partnerAutocomplete = useShipmentsStore(
    (state) => state.partnerAutocomplete
  );

  const [searchTerms, setSearchTerms] = useState(() => {
    const terms: Record<string, any> = {};
    fields.forEach((field) => {
      if (field.key !== 'partnerIds' && field.key !== 'partnerId') {
        if (initialParams[field.key]) {
          terms[field.key] = initialParams[field.key];
        } else if (storeSearchTerms[field.key]) {
          terms[field.key] = storeSearchTerms[field.key];
        } else {
          terms[field.key] = '';
        }
      }
    });

    return terms;
  });

  const [partnerIdsSelected, setPartnerIdsSelected] = useState<any>(() => {
    try {
      if (initialParams.partners) {
        return Array.isArray(initialParams.partners)
          ? initialParams.partners
          : JSON.parse(initialParams.partners);
      }
    } catch (e) {
      console.warn('Error while reading partners from URL', e);
    }

    return Array.isArray(partnerAutocomplete)
      ? partnerAutocomplete.map((partner: { name: string; id: string }) => ({
          label: partner.name,
          value: partner.id,
        }))
      : [];
  });

  const [isInitialSearch, setIsInitialSearch] = useState<boolean>(true);
  const isHandlingPopstate = useRef(false);

  const partnerIDsFieldExists = useMemo(
    () => fields.some((field) => field.key === 'partnerIds'),
    [fields]
  );

  const partnerIDFieldExists = useMemo(
    () => fields.some((field) => field.key === 'partnerId'),
    [fields]
  );

  const prepareSearchCriteria = useCallback(
    (terms: Record<string, any>, partners: any[]) => ({
      ...terms,
      ...(partnerIDsFieldExists && {
        partnerIds: partners.map((p) => p.value),
      }),
      ...(partnerIDFieldExists && { partnerId: partners.map((p) => p.value) }),
    }),
    [partnerIDsFieldExists, partnerIDFieldExists]
  );

  const lastSearchRef = useRef<{
    terms: Record<string, any>;
    partners: any[];
  } | null>(null);

  const performSearch = useCallback(
    (terms: Record<string, any>, partners: any[]) => {
      lastSearchRef.current = { terms, partners };

      const urlParams = {
        ...terms,
        ...(partners.length > 0 && {
          partners: JSON.stringify(partners),
        }),
      };

      updateURLParams(urlParams);

      const mergeCriterias = prepareSearchCriteria(terms, partners);
      dispatch({ type: 'updateSearchTerms', args: mergeCriterias });

      if (setSelectedRowKeys) {
        setSelectedRowKeys([]);
      }
    },
    [prepareSearchCriteria, dispatch, setSelectedRowKeys]
  );

  useEffect(() => {
    if (!isInitialSearch) return;

    const hasInitialParams = Object.keys(initialParams).length > 0;
    const hasPartners = partnerIdsSelected.length > 0;

    if (hasInitialParams || hasPartners) {
      const trimmedTerms = trimSearchTerms(searchTerms);

      const timeoutId = setTimeout(() => {
        performSearch(trimmedTerms, partnerIdsSelected);
        setIsInitialSearch(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [
    initialParams,
    searchTerms,
    partnerIdsSelected,
    performSearch,
    isInitialSearch,
  ]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setSearchTerms((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleCountryCodeChange = useCallback((option: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      countryCode: option,
    }));
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (isHandlingPopstate.current) return;

      const trimmedTerms = trimSearchTerms(searchTerms);
      performSearch(trimmedTerms, partnerIdsSelected);
    },
    [searchTerms, partnerIdsSelected, performSearch]
  );

  const handleReset = useCallback(() => {
    if (isHandlingPopstate.current) return;

    const emptyTerms = fields.reduce(
      (acc, field) => {
        acc[field.key] = '';

        return acc;
      },
      {} as Record<string, any>
    );

    setSearchTerms(emptyTerms);
    setPartnerIdsSelected([]);
    updateURLParams({});

    performSearch(emptyTerms, []);
  }, [fields, performSearch]);

  const handlePartnerSelect = useCallback(
    (partners: any[]) => {
      setPartnerIdsSelected(partners);
      const trimmedTerms = trimSearchTerms(searchTerms);
      performSearch(trimmedTerms, partners);
    },
    [searchTerms, performSearch]
  );

  return (
    <Stack component="form" onSubmit={handleSubmit} rowGap="0.75rem">
      <Text variant="titleM">{label}</Text>
      <SearchContainer>
        <FieldsGrid $columns={Math.min(fields.length, MAX_COLUMNS)}>
          {fields.map((field: IField) => {
            if (field.key === 'countryCode') {
              return (
                <CountrySelector
                  key={field.key}
                  name="country"
                  onChange={handleCountryCodeChange}
                  countryCodeValue={searchTerms[field.key]}
                  placeholder="Country"
                  width="100%"
                />
              );
            }
            if (field.key === 'partnerIds' || field.key === 'partnerId') {
              return (
                <PartnerAutocomplete
                  key={field.key}
                  placeholder={field.placeholder}
                  style={{ width: '100%' }}
                  updateSearch={handlePartnerSelect}
                  isMultiple={field.key === 'partnerIds'}
                  initialValue={partnerIdsSelected}
                  name={field.key}
                />
              );
            }

            return (
              <SearchField
                key={field.key}
                value={searchTerms[field.key] || ''}
                name={field.key}
                placeholder={field.placeholder}
                label={field.label}
                handleChange={handleChange}
                width="100%"
              />
            );
          })}
        </FieldsGrid>
        <ButtonsColumn>
          <SearchButton />
          <SearchResetButton handleReset={handleReset} />
          {extraButtons}
        </ButtonsColumn>
      </SearchContainer>
    </Stack>
  );
};

export default ServerSearch;
